/**
 * Kompakter Kontext für Betty Live — 1:1 Zahlen aus leistungCalculationHelper
 * (gleiche Pipeline wie ExecutiveSummaryModal / LeistungPage).
 */

import useAppStore from '../store';
import { calculateAllMetrics } from '../store/selectors/calculations';
import {
  aggregateHalbjahrSnapshot,
  buildNormalizedMonthsForEmployee,
  monthToSnapshotLine,
  type LeistungCalcContext,
  type NormalizedMonthData,
} from '../utils/leistungCalculationHelper';

const MAX_CHARS = 25000;

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mrz',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
] as const;

const formatEuro = (n: number): string => Math.round(n).toLocaleString('de-DE');

const formatSignedEuro = (n: number): string => {
  const abs = formatEuro(Math.abs(n));
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return abs;
};

function formatMonthLine(label: string, ziel: number, ergebnis: number, differenz: number, ampel: string): string {
  return `- ${label}: Ziel ${formatEuro(ziel)} € | Ergebnis ${formatEuro(ergebnis)} € | Differenz ${formatSignedEuro(differenz)} € | Ampel: ${ampel}`;
}

function formatHjLine(
  label: string,
  snap: { ziel: number; ergebnis: number; differenz: number; ampel: string }
): string {
  return `- ${label}: Ziel ${formatEuro(snap.ziel)} € | Ergebnis ${formatEuro(snap.ergebnis)} € | Differenz ${formatSignedEuro(snap.differenz)} € | Status: ${snap.ampel}`;
}

function resolveUserName(cd: Record<string, unknown>): string {
  const vorname = String(cd.vorname || '').trim();
  if (vorname) return vorname;
  const inhaber = String(cd.inhaber || '').trim();
  if (inhaber) return inhaber;
  const unternehmer = String(cd.unternehmername || '').trim();
  if (unternehmer) {
    const first = unternehmer.split(/\s+/)[0];
    if (first) return first;
  }
  return 'Mandantin';
}

function buildCalcContextForEmployee(
  employee: any,
  clientData: any,
  settings: any,
  metrics: any,
  mwstFaktor: number
): LeistungCalcContext {
  const id = String(employee?.id || '').trim();
  return {
    employee,
    leistung: clientData?.mitarbeiterLeistung?.[id] || {},
    lohnfaktorData: clientData?.lohnfaktorData || {},
    globalSettings: settings?.global || {},
    countrySettings: settings?.country || {},
    leistungsprovision: clientData?.leistungsprovision || {},
    kalkulatorState: clientData?.kalkulator || {},
    isHJ1GesperrtGlobal: Boolean(clientData?.isHJ1GesperrtGlobal),
    isHJ2Freigegeben: Boolean(clientData?.isHJ2Freigegeben),
    isTeamBonusMode: clientData?.teamBonus?.bonusMode === 'team',
    mwstFaktor,
    lohnfaktorAktiv: Number(metrics.lohnfaktorAktiv) || 0,
    geraeteListe: Array.isArray(metrics.geraeteListe) ? metrics.geraeteListe : [],
    baseMaterialProzent: Number(metrics.baseMaterialProzent) || 0,
    gemeinkostenPercent: Number(metrics.gemeinkostenPercent) || 0,
    avgSozialabgabenPercent: Number(metrics.avgSozialabgabenPercent) || 0,
    lohnfortzahlungPercent: Number(metrics.lohnfortzahlungPercent) || 0,
    verlustPercent: Number(metrics.verlustPercent) || 0,
    storeSettings: settings || {},
  };
}

/**
 * Liest Mandanten-Umsatzdaten und baut den Live-Context mit Original-System-Werten.
 */
export function buildSalonLiveContext(): string {
  try {
    const state = useAppStore.getState() as {
      clientData?: Record<string, unknown>;
      settings?: any;
    };
    const cd = (state?.clientData || {}) as Record<string, any>;
    const settings = state?.settings || {};
    const metrics: any = calculateAllMetrics(cd, settings);

    const userName = resolveUserName(cd);
    const salon =
      String(cd.firmenname || '').trim() ||
      String(cd.salonname || '').trim() ||
      'Unbekannt';

    const resolvedMwst =
      metrics?.mwstSatz !== undefined && metrics?.mwstSatz !== null
        ? Number(metrics.mwstSatz)
        : cd.isVatExempt
          ? 0
          : (Number(settings?.country?.MwSt_Satz) ?? 19);
    const mwstFaktor = 1 + resolvedMwst / 100;

    const personalData = Array.isArray(cd.personalData) ? cd.personalData : [];

    const lines: string[] = [
      `AKTIVE MANDANTIN / GESPRÄCHSPARTNERIN: ${userName}`,
      `SALON: ${salon}`,
      '',
      'DIREKTIVE FÜR BEGRÜSSUNG & ANTWORTEN:',
      `1. BEGRÜSSUNG: Begrüße die Mandantin immer persönlich mit ihrem Vornamen (${userName})!`,
      '2. KEINE EIGENEN HOCHRECHNUNGEN: Nenne keine erfundenen Jahresumsätze!',
      '3. ANTWORT-SCHEMA BEI FRAGEN ZU MONATEN / MITARBEITERN:',
      '   Nenne bei Fragen zu einem Mitarbeiter und Monat IMMER und AUSSCHLIESSLICH diese 4 Werte in einem prägnanten Satz:',
      '   - 1. Ziel (Netto)',
      '   - 2. Ergebnis (Netto)',
      '   - 3. Differenz (Plus/Minus)',
      '   - 4. Ampel-Status (Rentabel / Kostendeckend / Unter Kostendeckung)',
      '   Beispiel: "Im Juni lag das Ziel von Waltraut bei 4.000 €. Sie hat 3.200 € erreicht. Das ist ein Minus von 800 €. Damit war sie unter Kostendeckung."',
      '',
      'MITARBEITER-AUSWERTUNG (ORIGINAL-SYSTEM-WERTE):',
    ];

    if (personalData.length === 0) {
      lines.push('Keine Mitarbeiterdaten geladen.');
    }

    let waltrautMonths: NormalizedMonthData[] | null = null;
    let waltrautName = '';

    for (const raw of personalData) {
      const emp = raw as { id?: string; name?: string };
      const id = String(emp?.id || '').trim();
      const name = String(emp?.name || '').trim() || 'Ohne Name';
      if (!id) {
        lines.push('');
        lines.push(`Mitarbeiter: ${name}`);
        lines.push('- keine ID');
        continue;
      }

      const ctx = buildCalcContextForEmployee(emp, cd, settings, metrics, mwstFaktor);
      const months = buildNormalizedMonthsForEmployee(ctx);

      // 12 Slots sicherstellen (fehlende = 0)
      const byIndex = new Map<number, NormalizedMonthData>();
      months.forEach((m) => byIndex.set(m.index, m));

      lines.push('');
      lines.push(`Mitarbeiter: ${name}`);

      for (let i = 0; i < 12; i++) {
        const m = byIndex.get(i) || {
          monatName: MONTH_SHORT[i],
          index: i,
          bedienumsatzBrutto: 0,
          vkUmsatzBrutto: 0,
          gesamtBrutto: 0,
          gesamtNetto: 0,
          mindestNetto: 0,
          kostendeckungNetto: 0,
          bonusBedienung: 0,
          bonusVK: 0,
          isLocked: false,
          activeLohnfaktor: 0,
          urlaub: 0,
          krank: 0,
        };
        const snap = monthToSnapshotLine(m, MONTH_SHORT[i]);
        lines.push(
          formatMonthLine(snap.label, snap.ziel, snap.ergebnis, snap.differenz, snap.ampel)
        );
      }

      const hj1 = months.filter((m) => m.index < 6);
      const hj2 = months.filter((m) => m.index >= 6);
      const hj1Snap = aggregateHalbjahrSnapshot(hj1);
      const hj2Snap = aggregateHalbjahrSnapshot(hj2);
      lines.push(formatHjLine('Halbjahr 1', hj1Snap));
      lines.push(formatHjLine('Halbjahr 2', hj2Snap));

      const nLower = name.toLowerCase();
      if (nLower.includes('waltraut') || nLower.includes('waltraud')) {
        waltrautMonths = months;
        waltrautName = name;
      }
    }

    // --- DATEN-AUDIT (Waltraut) ---
    console.log('================ [BETTY LIVE WALTRAUT AUDIT] ================');
    if (waltrautMonths) {
      console.log('GEFUNDEN:', waltrautName);
      waltrautMonths.forEach((m) => {
        const snap = monthToSnapshotLine(m, MONTH_SHORT[m.index] || m.monatName);
        console.log(
          `${snap.label}: Ziel ${Math.round(snap.ziel)} | Ergebnis ${Math.round(snap.ergebnis)} | Diff ${Math.round(snap.differenz)} | ${snap.ampel}`
        );
      });
    } else {
      console.log('Waltraut NICHT gefunden. MA:', personalData.length);
      console.log(
        'Namen:',
        personalData.map((r) => String((r as { name?: string })?.name || '?')).join(' | ')
      );
    }
    console.log('============================================================');

    let out = lines.join('\n');
    const employeeCount = personalData.length;
    if (out.length > MAX_CHARS) {
      out = `${out.slice(0, MAX_CHARS - 1)}…`;
    }
    console.log('================ [BETTY LIVE CONTEXT SIZE] ================');
    console.log('Mitarbeiter erfasst:', employeeCount);
    console.log('salonContext Länge (Zeichen):', out.length);
    console.log('Limit:', MAX_CHARS, out.length >= MAX_CHARS ? '(KAPPUNG aktiv)' : '(unkaptiert)');
    console.log('============================================================');
    console.log('================ [BETTY LIVE CONTEXT INHALT] ================');
    console.log(out);
    console.log('============================================================');
    return out;
  } catch (err) {
    console.warn('[bettyLiveContext] buildSalonLiveContext fehlgeschlagen:', err);
    return '';
  }
}

/** Vorname für Token-Payload (userName). */
export function resolveLiveUserName(): string {
  try {
    const state = useAppStore.getState() as { clientData?: Record<string, unknown> };
    return resolveUserName(state?.clientData || {});
  } catch {
    return 'Mandantin';
  }
}

/** Screen-Label für Token-Call; Fallback wie im Auftrag. */
export function normalizeScreenContext(screen?: string | null): string {
  const s = String(screen || '').trim();
  return s || 'Standard Dashboard';
}
